const ResizePlugin = (slider) => {
  const observer = new ResizeObserver(function () {
    slider.update();
  });

  slider.on('created', () => {
    observer.observe(slider.container);
  });

  slider.on('destroyed', () => {
    observer.unobserve(slider.container);
  });
};

/**
 * PDP gallery only: use 2D translates on mobile to avoid iOS Safari pinch-zoom jetsam.
 * Uses renderMode: 'custom' so other Keen sliders keep the default translate3d renderer.
 */
const GallerySafariSafeTransformPlugin = (slider) => {
  const mql = window.matchMedia('(max-width: 989px)');

  const clearSlideStyles = (slide) => {
    slide.style.minWidth = '';
    slide.style.maxWidth = '';
    slide.style.minHeight = '';
    slide.style.maxHeight = '';
    slide.style.transform = '';
    slide.style.webkitTransform = '';
  };

  const apply = () => {
    if (slider.options.disabled) {
      slider.slides.forEach(clearSlideStyles);
      return;
    }

    const details = slider.track.details;
    if (!details) return;

    const vertical = !!slider.options.vertical;
    const size = slider.size;
    const use2d = mql.matches;
    let offset = 0;

    slider.slides.forEach((slide, idx) => {
      const slideDetails = details.slides[idx];
      if (!slideDetails) return;

      const slideSize = slideDetails.size * size;
      if (slider.options.slides?.perView !== 'auto') {
        if (vertical) {
          slide.style.minHeight = `${slideSize}px`;
          slide.style.maxHeight = `${slideSize}px`;
        } else {
          slide.style.minWidth = `${slideSize}px`;
          slide.style.maxWidth = `${slideSize}px`;
        }
      }

      const distance = slideDetails.distance * size - offset;
      offset += slideSize;

      const transform = use2d
        ? vertical
          ? `translateY(${distance}px)`
          : `translateX(${distance}px)`
        : vertical
          ? `translate3d(0, ${distance}px, 0)`
          : `translate3d(${distance}px, 0, 0)`;

      slide.style.transform = transform;
      slide.style.webkitTransform = transform;
    });
  };

  slider.on('created', apply);
  slider.on('detailsChanged', apply);
  slider.on('updated', apply);
  slider.on('optionsChanged', apply);
  mql.addEventListener('change', apply);

  slider.on('destroyed', () => {
    mql.removeEventListener('change', apply);
    slider.slides.forEach(clearSlideStyles);
  });
};

class KeenSliderComponent extends HTMLElement {
  constructor() {
    super();

    requestAnimationFrame(() => {
      this.initializeSlider();
    });
  }

  initializeSlider() {
    this.sliderId = this.querySelector('[id^="Slider-"]')?.id;
    this.sliderThumbnailsId = this.querySelector('[id^="Slider-Thumbnails-"]')?.id;
    this.currentCounter = this.querySelector('.slider-counter--current');
    this.counterDots = this.querySelectorAll('.slider-counter__link');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.autoplayButtonIsSetToPlay = false;
    const plugins = this.dataset.responsive === 'true' ? [ResizePlugin] : [];
    this.adaptiveHeight = this.dataset.adaptiveHeight || false;
    const isMobileDisabled = this.dataset.sliderMobileDisabled === 'true';
    const isTabletDisabled = this.dataset.sliderTabletDisabled === 'true';
    const isDesktopDisabled = this.dataset.sliderDesktopDisabled === 'true';
    const isDesktopLargeDisabled = this.dataset.sliderDesktopLargeDisabled === 'true';

    setTimeout(() => {
      this.createSlider(plugins, isMobileDisabled, isTabletDisabled, isDesktopDisabled, isDesktopLargeDisabled);
    }, 0);
  }

  createSlider(plugins, isMobileDisabled, isTabletDisabled, isDesktopDisabled, isDesktopLargeDisabled) {
    const isProductGallery = this.id?.startsWith('GalleryViewer-');
    if (isProductGallery) {
      plugins = [...plugins, GallerySafariSafeTransformPlugin];
    }

    this.slider = new KeenSlider(
      `#${this.sliderId}`,
      {
        loop: this.dataset.sliderLoop == 'true' ? true : false,
        ...(isProductGallery ? { renderMode: 'custom' } : {}),
        slides: {
          origin: this.dataset.slidesOriginMobile ? this.dataset.slidesOriginMobile : this.dataset.slidesOrigin,
          perView: this.dataset.slidesPerViewMobile,
          spacing: this.dataset.sliderGapMobile,
        },
        disabled: isMobileDisabled || false,
        breakpoints: {
          '(min-width: 750px)': {
            slides: {
              origin: this.dataset.slidesOriginTablet ? this.dataset.slidesOriginTablet : this.dataset.slidesOrigin,
              perView: this.dataset.slidesPerViewTablet || this.dataset.slidesPerViewDesktop,
              spacing: this.dataset.sliderGapMobile || this.dataset.sliderGapDesktop,
            },
            disabled: isTabletDisabled || false,
          },
          '(min-width: 990px)': {
            slides: {
              origin: this.dataset.slidesOriginDesktop ? this.dataset.slidesOriginDesktop : this.dataset.slidesOrigin,
              perView: this.dataset.slidesPerViewDesktop,
              spacing: this.dataset.sliderGapDesktop,
            },
            disabled: isDesktopDisabled || false,
          },
          '(min-width: 1800px)': {
            slides: {
              origin: this.dataset.slidesOriginDesktopLarge
                ? this.dataset.slidesOriginDesktopLarge
                : this.dataset.slidesOrigin,
              perView: this.dataset.slidesPerViewDesktopLarge
                ? this.dataset.slidesPerViewDesktopLarge
                : this.dataset.slidesPerViewDesktop,
              spacing: this.dataset.sliderGapDesktopLarge
                ? this.dataset.sliderGapDesktopLarge
                : this.dataset.sliderGapDesktop,
            },
            disabled: isDesktopLargeDisabled || false,
          },
        },
        created: (sliderInstance) => {
          requestAnimationFrame(() => {
            sliderInstance.container.classList.add('slider-created');
            this.resetButtonsVisibility();
          });
        },
        updated: (sliderInstance) => {
          this.resetButtonsVisibility();
        },
      },
      plugins
    );

    requestAnimationFrame(() => {
      this.setupSliderObservers();
      this.setupThumbnails();
      this.setupEventListeners();
      this.init();
    });
  }

  setupSliderObservers() {
    // MutationObserver to update slider on DOM changes (slides added/removed)
    const slideList = this.querySelector(`#${this.sliderId}`);
    if (slideList) {
      this._mutationObserver = new MutationObserver(() => {
        this.slider.update();
      });
      this._mutationObserver.observe(slideList, {
        childList: true, // Watch for added/removed slides
        subtree: false, // Only direct children (the slides)
      });
    }
  }

  setupThumbnails() {
    if (this.dataset.hasThumbnailSlider) {
      this.thumbnails = new KeenSlider(
        `#${this.sliderThumbnailsId}`,
        {
          initial: 0,
          slides: {
            perView: this.dataset.thumbnailSlidesPerViewDesktop,
            spacing: this.dataset.thumbnailSliderGapMobile,
          },
          breakpoints: {
            '(min-width: 750px)': {
              slides: {
                perView: this.dataset.thumbnailSlidesPerViewTablet,
                spacing: this.dataset.thumbnailSliderGapTablet,
              },
            },
          },
        },
        [this.createThumbnailPlugin(this.slider)]
      );
    }
  }

  setupEventListeners() {
    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.counterDots?.forEach((counter, index) => {
      counter.addEventListener('click', () => {
        this.onCounterClick(index);
      });
    });
    this.slider.on('slideChanged', this.onSlideChanged.bind(this));
  }

  init() {
    this.counterDotsUpdateProgress();
    if (this.adaptiveHeight && window.matchMedia('(max-width: 749px)').matches) this.updateHeight();

    this.handleVideoPlayback();
    this.setupMobileGalleryMemoryFix();

    // defer autoplay until full page is loaded
    if (this.dataset.sliderAutoplay == 'true') {
      window.addEventListener('load', () => {
        this.setAutoPlay();
      });
    }
  }

  isMobileGalleryViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  setupMobileGalleryMemoryFix() {
    if (!this.id?.startsWith('GalleryViewer-') || !this.slider) return;

    // Cull only after the slider has measured slide sizes
    requestAnimationFrame(() => this.cullDistantGalleryImages());

    this._galleryMobileMql = window.matchMedia('(max-width: 989px)');
    this._galleryMobileMqlHandler = () => {
      if (this._galleryMobileMql.matches) {
        this.cullDistantGalleryImages();
      } else {
        this.restoreCulledGalleryImages();
      }
    };
    this._galleryMobileMql.addEventListener('change', this._galleryMobileMqlHandler);

    // Prefetch slides that become visible during drag / before snap
    this._galleryDetailsHandler = () => {
      if (!this.isMobileGalleryViewport() || !this.slider?.track?.details) return;
      const details = this.slider.track.details;
      let centerIndex = details.rel;
      let maxPortion = 0;

      details.slides.forEach((slide, index) => {
        if (slide.portion > maxPortion) {
          maxPortion = slide.portion;
          centerIndex = index;
        }
      });

      this.restoreGalleryImagesAround(centerIndex);
    };
    this.slider.on('detailsChanged', this._galleryDetailsHandler);

    this._galleryAnimationHandler = () => {
      if (!this.isMobileGalleryViewport() || !this.slider?.track?.details) return;
      const next = this.slider.animator?.targetIdx;
      if (next == null) return;
      this.restoreGalleryImagesAround(this.slider.track.absToRel(next));
    };
    this.slider.on('animationStarted', this._galleryAnimationHandler);
  }

  teardownMobileGalleryMemoryFix() {
    if (this._galleryMobileMql && this._galleryMobileMqlHandler) {
      this._galleryMobileMql.removeEventListener('change', this._galleryMobileMqlHandler);
    }
    // keen-slider: on(event, fn, true) removes the listener
    if (this.slider && this._galleryDetailsHandler) {
      this.slider.on('detailsChanged', this._galleryDetailsHandler, true);
    }
    if (this.slider && this._galleryAnimationHandler) {
      this.slider.on('animationStarted', this._galleryAnimationHandler, true);
    }
    this._galleryMobileMql = null;
    this._galleryMobileMqlHandler = null;
    this._galleryDetailsHandler = null;
    this._galleryAnimationHandler = null;
  }

  updateHeight = () => {
    this.slider.container.style.height =
      this.slider.slides[this.slider.track.details?.rel]?.querySelector('.product-media-container').offsetHeight + 'px';
  };

  createThumbnailPlugin(main) {
    return (slider) => {
      function removeActive() {
        slider.slides.forEach((slide) => {
          slide.classList.remove('active');
        });
      }
      function addActive(idx) {
        slider.slides[idx].classList.add('active');
      }

      function addClickEvents() {
        slider.slides.forEach((slide, idx) => {
          slide.addEventListener('click', () => {
            main.moveToIdx(idx);
          });
        });
      }

      slider.on('created', () => {
        addActive(slider.track.details.rel);
        addClickEvents();
        main.on('animationStarted', (main) => {
          removeActive();
          const next = main.animator.targetIdx || 0;
          addActive(main.track.absToRel(next));
          slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
        });
      });
    };
  }

  onButtonClick(event) {
    if (this.dataset.sliderMoveEntireRow == 'true') {
      this.moveEntireRow(event.currentTarget.name);
      return;
    }

    this.moveOneSlide(event.currentTarget.name);
  }

  moveOneSlide(target) {
    const { rel, minIdx, maxIdx } = this.slider.track.details;
    if (target === 'next') {
      if (maxIdx === rel) return;
      if (maxIdx !== Infinity) this.updateButtonsVisibility(rel + 1);
      this.slider.moveToIdx(rel + 1);
    }
    if (target === 'previous') {
      if (minIdx === rel) return;
      if (minIdx !== -Infinity) this.updateButtonsVisibility(rel - 1);
      this.slider.moveToIdx(rel - 1);
    }
  }

  moveEntireRow(target) {
    let nextSlideIdx;
    let sliderLoop = this.dataset.sliderLoop;
    if (target === 'next') {
      if (sliderLoop == 'true') {
        nextSlideIdx = this.slider.track.details.rel + Math.floor(this.slider.options.slides.perView);
      } else {
        nextSlideIdx =
          this.slider.track.details.maxIdx <
          this.slider.track.details.rel + Math.floor(this.slider.options.slides.perView)
            ? this.slider.track.details.maxIdx
            : this.slider.track.details.rel + Math.floor(this.slider.options.slides.perView);
      }
      this.slider.moveToIdx(nextSlideIdx);
    }

    if (target === 'previous') {
      if (sliderLoop == 'true') {
        nextSlideIdx = this.slider.track.details.rel - Math.floor(this.slider.options.slides.perView);
      } else {
        nextSlideIdx =
          this.slider.track.details.rel - Math.floor(this.slider.options.slides.perView) < 0
            ? 0
            : this.slider.track.details.rel - Math.floor(this.slider.options.slides.perView);
      }
      this.slider.moveToIdx(nextSlideIdx);
    }
  }

  onCounterClick(index) {
    this.slider.moveToIdx(index);
  }

  onSlideChanged(event) {
    // Only reset the autoplay if autoplay is enabled and the user manually changed slides
    if (this.dataset.sliderAutoplay == 'true' && this.autoplayButtonIsSetToPlay) {
      this.resetAutoplay();
    }

    if (this.currentCounter) {
      this.currentCounter.textContent = this.slider.track.details.rel + 1;
    }

    if (this.counterDots.length > 0) {
      this.counterDots.forEach((dot) => {
        dot.classList.remove('slider-counter__link--active');
        dot.removeAttribute('aria-current');
      });
      this.counterDots[this.slider.track.details.rel].classList.add('slider-counter__link--active');
      this.counterDots[this.slider.track.details.rel].setAttribute('aria-current', 'true');
    }

    this.counterDotsUpdateProgress();
    if (this.adaptiveHeight && window.matchMedia('(max-width: 749px)').matches) this.updateHeight();

    this.handleVideoPlayback();
    this.cullDistantGalleryImages();
  }

  /**
   * Mobile only: drop decoded bitmaps for slides far from the active one.
   * Keeps slide boxes sized via aspect-ratio padding; only swaps <img> sources.
   */
  cullDistantGalleryImages() {
    if (!this.id?.startsWith('GalleryViewer-')) return;
    if (!this.isMobileGalleryViewport()) return;
    if (!this.slider?.slides || !this.slider.track?.details) return;
    if (this.slider.options?.disabled) return;

    this.restoreGalleryImagesAround(this.slider.track.details.rel, true);
  }

  restoreGalleryImagesAround(centerIndex, cullOthers = false) {
    if (!this.slider?.slides) return;

    // Keep a slightly wider window so fast swipes don't flash placeholders
    const keepDistance = 2;

    this.slider.slides.forEach((slide, index) => {
      const keep = Math.abs(index - centerIndex) <= keepDistance;
      const images = slide.querySelectorAll('.product__media img, .product-media-container img');

      images.forEach((img) => {
        if (keep) {
          this.restoreCulledImage(img);
          return;
        }

        if (cullOthers) this.cullImage(img);
      });
    });
  }

  cullImage(img) {
    if (img.dataset.cullSrc != null) return;

    img.dataset.cullSrc = img.getAttribute('src') || '';
    img.dataset.cullSrcset = img.getAttribute('srcset') || '';
    img.removeAttribute('srcset');
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  }

  restoreCulledImage(img) {
    if (img.dataset.cullSrc != null) {
      img.src = img.dataset.cullSrc;
      delete img.dataset.cullSrc;
    }
    if (img.dataset.cullSrcset != null) {
      img.srcset = img.dataset.cullSrcset;
      delete img.dataset.cullSrcset;
    }
  }

  restoreCulledGalleryImages() {
    if (!this.slider?.slides) return;

    this.slider.slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((img) => this.restoreCulledImage(img));
    });
  }

  handleVideoPlayback() {
    if (!this.slider?.slides || !this.slider.track?.details) return;

    const currentSlide = this.slider.slides[this.slider.track.details.rel];
    if (!currentSlide) return;

    this.pauseAllVideos();

    const video = currentSlide.querySelector('video');
    if (video && video.hasAttribute('autoplay')) {
      setTimeout(() => {
        if (this.slider.track.details.rel === this.slider.track.details.abs) {
          video.play().catch((error) => {
            console.warn('Video autoplay failed:', error);
          });
        }
      }, 100);
    }
  }

  pauseAllVideos() {
    const allVideos = this.querySelectorAll('video');
    allVideos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  // Handle resetting the autoplay timer
  resetAutoplay() {
    clearInterval(this.autoplay);
    if (this.autoplayButtonIsSetToPlay) {
      this.play();
    }
  }

  setAutoPlay() {
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton?.addEventListener('click', this.autoPlayToggle.bind(this));
    }
    this.autoplayButtonIsSetToPlay = true;
    this.play();
    this.counterDotsUpdateProgress();
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton && document.activeElement !== this.sliderAutoplayButton) {
      if (!this.autoplayButtonIsSetToPlay) return;
      this.play();
    }
  }

  focusInHandling(event) {
    if (event.target.classList.contains('slider-button') || event.target.closest('.slider-button')) {
      return;
    }

    if (this.sliderAutoplayButton && event.target === this.sliderAutoplayButton) {
      if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    }
  }

  resetButtonsVisibility() {
    if (!this.slider || !this.prevButton || !this.nextButton) return;

    const allSlidesFit = this.slider.slides.length <= this.slider.options.slides.perView;
    this.slider.container.classList.toggle('slider--all-slides-fit', allSlidesFit);
    if (allSlidesFit) {
      this.prevButton.classList.add('visibility-hidden');
      this.nextButton.classList.add('visibility-hidden');
      return;
    }

    if (this.slider.options.loop === false) {
      const { rel, minIdx, maxIdx } = this.slider.track.details;
      this.prevButton.classList.toggle('visibility-hidden', rel === minIdx);
      this.nextButton.classList.toggle('visibility-hidden', rel === maxIdx);
    } else {
      // buttons visible for looped (infinite) slider
      this.prevButton.classList.remove('visibility-hidden');
      this.nextButton.classList.remove('visibility-hidden');
    }
  }
  updateButtonsVisibility(relativeIndex) {
    const { minIdx, maxIdx } = this.slider.track.details;
    this.prevButton.classList.toggle('visibility-hidden', relativeIndex === minIdx);
    this.nextButton.classList.toggle('visibility-hidden', relativeIndex === maxIdx);
  }
  play() {
    const allSlidesFit = this.slider.slides.length <= this.slider.options.slides.perView;
    if (allSlidesFit) {
      this.pause();
      return;
    }
    this.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(() => {
      if (this.dataset.sliderMoveEntireRow == 'true') {
        this.moveEntireRow('next');
        return;
      }
      this.moveOneSlide('next');
    }, this.dataset.sliderAutoplaySpeed);
  }

  pause() {
    this.setAttribute('aria-live', 'polite');
    this.autoplay = clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  counterDotsUpdateProgress() {
    if (this.counterDots.length < 1) return;
    if (this.dataset.sliderAutoplay === 'false' || !this.autoplayButtonIsSetToPlay) return;

    clearInterval(this.countdown);
    this.sliderDotProgress = this.querySelector('.slider-counter__link--active .dot-progress');
    this.sliderDotProgress.style.width = 0;

    let max = Number(this.dataset.sliderAutoplaySpeed);
    let width = 0;

    this.countdown = setInterval(() => {
      if (this.autoplay === undefined) return;
      if (width < max && parseInt(this.sliderDotProgress.style.width) < 100) {
        width = width + 50;
        let progress = (100 / max) * width;
        this.sliderDotProgress.style.width = `${progress}%`;
      }
    }, 50);
  }

  disconnectedCallback() {
    this.pauseAllVideos();
    this.teardownMobileGalleryMemoryFix();
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }
  }
}

customElements.define('keen-slider-component', KeenSliderComponent);

/*
  Simple and lightweight auto-scrolling vertical slider,
  leveraging the native CSS scroll-snap feature for smooth transitions.

  ## Basic HTML Structure:
  <div class="slider-simple__slides slider-simple--vertical js-slider-simple--vertical">
    <div class="slider-simple__slide">Slide 1</div>
    <div class="slider-simple__slide">Slide 2</div>
    <div class="slider-simple__slide">Slide 3</div>
  </div>
*/
// don't start until page is interactive
document.addEventListener('DOMContentLoaded', function () {
  function initializeSimpleSliders() {
    const sliders = document.querySelectorAll('.js-slider-simple--vertical');

    let sliderIndex = 0;
    const chunkSize = 2;

    function processSliderChunk() {
      const endIndex = Math.min(sliderIndex + chunkSize, sliders.length);

      for (let i = sliderIndex; i < endIndex; i++) {
        const slider = sliders[i];
        if (!slider || slider.children.length < 2) continue;

        const slideHeight = slider.offsetHeight;
        const slideCount = slider.children.length;
        let currentIndex = 0;

        function autoScroll() {
          currentIndex = (currentIndex + 1) % slideCount;
          slider.scrollTo({ top: currentIndex * slideHeight, behavior: 'smooth' });
        }

        setInterval(autoScroll, 5000);
      }

      sliderIndex = endIndex;

      if (sliderIndex < sliders.length) {
        setTimeout(processSliderChunk, 0);
      }
    }

    if (sliders.length > 0) {
      setTimeout(processSliderChunk, 0);
    }
  }

  setTimeout(initializeSimpleSliders, 0);
});
